import { forwardRef, useMemo } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'

import { ITableColumn } from '@makyoapp/makyo-sdk/models/Table'
import { isDateValid, RatingColOptionsOnly } from '@makyoapp/makyo-common'
import {
  ColorVariant,
  ColumnTypes,
  CornerRadius,
  getShapeClass,
  MaterialIcon,
  SelectDropdown,
} from '@makyoapp/makyo-tw-ui'

import { useCompBackgroundColor } from '@/context/__project__/CompContext'
import { UserColumnFilterType, UserColumnFilterValue } from '@/shared/builder/types/user-filter'

interface ChipV2Props {
  icon?: string
  value?: any
  label?: string
  onRemove?: () => void
  onClick?: () => void
  chipColor?: ColorVariant
  cornerRadius?: CornerRadius
}

/**
 * New Chip Component
 */
const ChipV2 = forwardRef<HTMLButtonElement, ChipV2Props>(
  ({ icon, value, label, onRemove, onClick, cornerRadius = 'medium', chipColor = 'primary-95' }, ref) => {
    const { currentParentBg } = useCompBackgroundColor()
    const isSameChipBg = currentParentBg === chipColor

    const isSelected = !!value

    return (
      <button
        ref={ref}
        type='button'
        className={clsx(
          'cursor-pointer flex items-center py-2 px-3 h-10 max-w-[225px] overflow-hidden',
          'outline-none ring-0 focus:ring-0 focus:outline-none',
          isSameChipBg && `border border-on-${chipColor}/50 transition-colors`,
          getShapeClass(cornerRadius),
          isSelected
            ? `bg-${chipColor} text-on-${chipColor}`
            : `bg-${chipColor} text-on-${chipColor} hover:bg-${chipColor}/90`
        )}
        onClick={onClick}
      >
        {icon && <MaterialIcon icon={icon} size={16} />}

        <div className='text-sm truncate mx-2'>{label}</div>

        {isSelected && (
          <div
            title='Remove'
            className={clsx(
              `ml-1 -mr-1.5 flex items-center justify-center w-5 h-5 p-2 rounded-full`,
              `transition-colors bg-transparent hover:bg-${chipColor}/30`
            )}
          >
            <MaterialIcon
              icon='close'
              size={16}
              className='cursor-pointer'
              onClick={(e) => {
                e.stopPropagation()
                onRemove?.()
              }}
            />
          </div>
        )}
      </button>
    )
  }
)
ChipV2.displayName = 'ChipV2'

interface CompFilterProps {
  setting: UserColumnFilterValue
  column: ITableColumn
  rowOptions: { id: string; value: any; label: string }[]
  value?: any
  onChange: (next?: any) => void
  onShowFilter: () => void
  chipColor?: ColorVariant
  cornerRadius?: CornerRadius
}

/**
 * `FilterChip` is a function component that provides an interface for column filter.
 */
export function FilterChip({
  setting,
  column,
  rowOptions,
  value,
  onChange,
  onShowFilter,
  chipColor = 'primary-95',
  cornerRadius = 'medium',
}: CompFilterProps) {
  const { filterType, multiple } = setting

  const colSettings = column._Settings as any

  const label = useMemo(() => {
    const defaultLabel = setting.label || column?._Name || ''
    if (value) {
      if (typeof value === 'boolean') {
        return defaultLabel
      }
      if (Array.isArray(value) && value.length > 0) {
        if (setting.filterType === UserColumnFilterType.NumberRange) {
          return `${value[0]} - ${value[1]}`
        }
        if (setting.filterType === UserColumnFilterType.DateDateRange) {
          const start = new Date(value[0])
          const end = new Date(value[1])

          if (isDateValid(start) && isDateValid(end)) {
            return `${dayjs(start).format('MMM DD')} - ${dayjs(end).format('MMM DD')}`
          } else {
            return `invalid date`
          }
        }
        return multiple ? `${defaultLabel} (${value.length})` : value[0]
      }
      if (typeof value === 'string') {
        if (setting.filterType === UserColumnFilterType.RatingAmountWithin) {
          const { ratingMax = 5 } = colSettings as RatingColOptionsOnly
          return value === ratingMax.toString() ? value : `${value}.0 - ${value}.9`
        }
        if (setting.filterType === UserColumnFilterType.DateExactDate) {
          const dt = new Date(value)
          return isDateValid(dt) ? `${dayjs(dt).format('MMM DD')}` : 'invalid date'
        }
        return value
      }
      if (typeof value === 'number') {
        return rowOptions.find((row) => row.value === value)?.label || defaultLabel
      }
    }
    return defaultLabel
  }, [setting.label, setting.filterType, column?._Name, value, multiple, colSettings, rowOptions])

  switch (filterType) {
    // ✅ ok
    case UserColumnFilterType.TextRadio:
    case UserColumnFilterType.TextSegmented:
      return (
        <ChipV2
          value={value}
          label={label}
          icon={setting.icon}
          onRemove={() => onChange(undefined)}
          onClick={() => onShowFilter()}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )

    // ✅ ok
    case UserColumnFilterType.NumberSelection:
    case UserColumnFilterType.TextSelection: {
      const currValue = multiple
        ? Array.isArray(value)
          ? value
          : []
        : Array.isArray(value)
          ? value[0]
          : typeof value === 'undefined' || value === null
            ? ''
            : value.toString()

      const options =
        column._Type === ColumnTypes.Number
          ? rowOptions.map((row) => ({
              id: row.value,
              label: row.label,
              value: row.value.toString(),
            }))
          : rowOptions

      return (
        <SelectDropdown
          multiple={multiple}
          options={options}
          value={currValue}
          onChange={(v) => {
            if (multiple) {
              onChange(column._Type === ColumnTypes.Number ? (v as any[]).map((v) => Number(v)) : v)
            } else {
              onChange(v)
            }
          }}
          customTrigger={
            <ChipV2
              value={value}
              label={label}
              icon={setting.icon}
              onRemove={() => onChange(undefined)}
              cornerRadius={cornerRadius}
              chipColor={chipColor}
            />
          }
          contentClassName='min-w-[150px]'
        />
      )
    }

    // ✅ ok
    case UserColumnFilterType.NumberExactAmount: {
      return (
        <ChipV2
          value={value}
          label={label}
          icon={setting.icon}
          onRemove={() => onChange(undefined)}
          onClick={() => onShowFilter()}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )
    }
    // ✅ ok
    case UserColumnFilterType.NumberRange: {
      return (
        <ChipV2
          value={value}
          label={label}
          icon={setting.icon}
          onRemove={() => onChange(undefined)}
          onClick={() => onShowFilter()}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )
    }

    // ✅ ok
    case UserColumnFilterType.DateExactDate: {
      return (
        <ChipV2
          value={value}
          label={label}
          icon={setting.icon}
          onRemove={() => onChange(undefined)}
          onClick={() => onShowFilter()}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )
    }
    // ✅ ok
    case UserColumnFilterType.DateDateRange: {
      return (
        <ChipV2
          value={value}
          label={label}
          icon={setting.icon}
          onRemove={() => onChange(undefined)}
          onClick={() => onShowFilter()}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )
    }

    // ✅ ok
    case UserColumnFilterType.RatingAmountWithin: {
      return (
        <ChipV2
          value={value}
          label={label}
          icon={setting.icon}
          onRemove={() => onChange(undefined)}
          onClick={() => onShowFilter()}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )
    }

    // ✅ ok
    case UserColumnFilterType.RatingAtLeast: {
      // format: {minumum, value}
      return (
        <ChipV2
          value={value}
          label={value ? value.value : label}
          icon={setting.icon}
          onRemove={() => onChange(undefined)}
          onClick={() => onShowFilter()}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )
    }

    // ✅ ok
    case UserColumnFilterType.ContentNotEmpty:
    case UserColumnFilterType.BooleanToggle: {
      return (
        <ChipV2
          value={value}
          label={label}
          icon={setting.icon}
          onRemove={() => {
            onChange(undefined)
          }}
          onClick={() => {
            let nextVal = value === true ? undefined : true
            onChange?.(nextVal)
          }}
          cornerRadius={cornerRadius}
          chipColor={chipColor}
        />
      )
    }

    default:
      return null
  }
}
